import { Component, OnInit, ViewChild } from "@angular/core";
import { AuthService } from "../../services/auth/auth.service";
import { IonSearchbar, NavController } from "@ionic/angular";
import { Storage } from "@ionic/storage-angular";
import { BehaviorSubject } from "rxjs";
import { MenuSliderComponent } from "../../components/menu-slider/menu-slider.component";
import { DocumentsService } from "../../services/documents/documents.service";
import { DocumentModel } from "../../entities/DocumentModel";
import { BrowserService } from "../../services/browser/browser.service";

@Component({
    selector: "app-documents",
    templateUrl: "./documents.page.html",
    styleUrls: ["./documents.page.scss"]
})
export class DocumentsPage implements OnInit {
    @ViewChild("search") search: IonSearchbar;
    @ViewChild("sortStateMenu") sortStateMenu: MenuSliderComponent;

    // Sort menu state
    private menuState = false;

    // Variables for which sort method is selected in the sort menu.
    public sortLastOpened = false;
    public sortLastChanged = false;
    public sortByName = true;
    public documentsToApprove = Array<DocumentModel>();

    // eslint-disable-next-line @typescript-eslint/ban-types
    public documents = Array<DocumentModel>();

    public lastOpenedDocument = [];
    // eslint-disable-next-line @typescript-eslint/ban-types
    public documentsBackup = Array<DocumentModel>();
    public isLoading = false;
    private currentPage = 1;
    fetched = new BehaviorSubject(false);

    constructor(
        private authService: AuthService,
        private documentService: DocumentsService,
        private storage: Storage,
        private navController: NavController,
        private browserService: BrowserService
    ) {
        this.lastOpenedDocument = [];
        this.storage.get("lastOpenedList").then((list) => {
            if (list !== null) {
                this.lastOpenedDocument = list;
            }
        });
    }

    showSortStateMenu() {
        setTimeout(() => {
            this.menuState = !this.menuState;

            if (this.menuState) {
                this.sortStateMenu.open();
            } else {
                this.sortStateMenu.close();
            }
        }, 50);
    }

    selectSortMethod(id: any) {
        switch (id) {
            // Sort by name
            case 1:
                this.storage.set("filterPreference", 1);
                this.documents.sort((a, b) => a.name.localeCompare(b.name));
                this.sortByName = true;
                this.sortLastOpened = false;
                this.sortLastChanged = false;
                break;

            // Sort by last changed
            case 2:
                this.storage.set("filterPreference", 2);
                // @ts-ignore
                this.documents.sort((a, b) => b.lastUpdate - a.lastUpdate);
                this.sortLastChanged = true;
                this.sortByName = false;
                this.sortLastOpened = false;
                break;

            // Sort by last opened
            case 3:
                this.filterOnLastOpened();
                this.storage.set("filterPreference", 3);
                this.documentsBackup = this.documents;
                this.sortLastOpened = true;
                this.sortByName = false;
                this.sortLastChanged = false;
                break;

            // Set the default to name sort.
            default:
                this.documents = this.documentsBackup;
                this.documents.sort((a, b) => a.name.localeCompare(b.name));
                this.storage.set("filterPreference", 1);
                this.sortByName = true;
                this.sortLastOpened = false;
                this.sortLastChanged = false;
                break;
        }
    }

    setLastOpenedDate(document: DocumentModel) {
        this.storage.get("lastOpenedList").then((list) => {
            if (list === null) {
                this.storage.set("lastOpenedList", []);
            }
        });

        const lastOpened = this.documents.find((doc) => {
            return doc === document;
        });

        // Checks if the clicked item is already in the lastOpenedDocument array.
        if (
            this.lastOpenedDocument !== null &&
            this.lastOpenedDocument.length > 0
        ) {
            for (const item of this.lastOpenedDocument) {
                const alreadyOpenedDocument = this.getLastOpenedDocumentByFind(
                    lastOpened.id,
                    lastOpened.type
                );

                // If it is not, then we'll add it to it so that it gets pushed into local storage.
                // Else we'll just update the timestamp.
                if (alreadyOpenedDocument === undefined) {
                    this.lastOpenedDocument.push({
                        id: lastOpened.id,
                        type: lastOpened.type,
                        lastOpened: lastOpened.lastOpened
                    });
                } else if (
                    alreadyOpenedDocument.id === lastOpened.id &&
                    alreadyOpenedDocument.type === lastOpened.type
                ) {
                    alreadyOpenedDocument.lastOpened = new Date(Date.now());
                }
            }
        } else {
            // Else we'll directly push the parameters into the array
            this.lastOpenedDocument.push({
                id: lastOpened.id,
                type: lastOpened.type,
                lastOpened: lastOpened.lastOpened
            });
        }

        this.storage.set("lastOpenedList", this.lastOpenedDocument);
    }

    getDocumentByFind(id: number, type: string) {
        return this.documents.find(
            (document) => document.id === id && document.type === type
        );
    }

    getLastOpenedDocumentByFind(id: number, type: string) {
        return this.lastOpenedDocument.find(
            (document) => document.id === id && document.type === type
        );
    }

    filterOnLastOpened() {
        for (const item of this.lastOpenedDocument) {
            const document = this.getDocumentByFind(item.id, item.type);
            if (document !== undefined) {
                document.lastOpened = item.lastOpened;
            }
        }

        // @ts-ignore
        this.documents.sort((a, b) => b.lastOpened - a.lastOpened);
    }

    refreshDocuments() {
        this.currentPage = 1;
        this.documents = [];
        this.fetchDocuments();
    }

    fetchDocuments() {
        this.isLoading = true;

        this.documentService.fetchDocuments().subscribe((data) => {
            data.forEach((item) => {
                item.lastOpened = null;
                this.documents.push(new DocumentModel(item));
            });
            this.storage.get("filterPreference").then((val) => {
                this.selectSortMethod(val);
            });
            this.fetched.next(true);
            this.currentPage++;
            this.isLoading = false;
        });
    }

    searchChange(): void {
        this.documents = Array<DocumentModel>();
        this.currentPage = 1;
        this.fetchDocuments();
    }

    ionViewWillEnter() {
        this.fetchDocumentsToApprove();

        this.storage.get("filterPreference").then((val) => {
            this.selectSortMethod(val);
        });

        this.storage.get("lastOpenedList").then((list) => {
            if (list !== null) {
                this.lastOpenedDocument = list;
            }
        });
    }

    ngOnInit() {
        this.currentPage = 1;
        this.refreshDocuments();
    }

    doRefresh(event) {
        setTimeout(() => {
            this.refreshDocuments();
            event.target.complete();
        }, 1000);
    }

    openLink(document: DocumentModel) {
        if (document.type === "map") {
            this.navController.navigateForward("/documents/" + document.id);
        } else if (document.type === "doc") {
            if (document.docType === "document") {
                this.navController.navigateForward(
                    "/preview/4/" + document.id,
                    {
                        queryParams: {
                            hasToAccept: document.hasToAccept,
                            isAcceptedByUser: document.isAcceptedByUser
                        }
                    }
                );
            } else if (document.docType === "url") {
                this.browserService.openLink(document.url);
            }
        }
    }

    fetchDocumentsToApprove() {
        this.documentsToApprove = [];

        this.documentService.fetchDocumentsToApprove().subscribe((data) => {
            data.data.forEach((item) => {
                this.documentsToApprove.push(new DocumentModel(item));
            });
        });
    }
}
